import { yupResolver } from '@hookform/resolvers/yup';
import { ERROR_MESSAGES } from 'constants/validation-error-messages';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { fetchDepartmentsPaginated, getWorkplaces, updateUser } from 'services/registration';
import { UserData } from 'types/user';
import { WorkplaceType } from 'views/registration/useRegistrationHook';
import * as yup from 'yup';

export interface FirstLoginFormValues {
    first_name: string;
    last_name: string;
    phone?: string;
    employee_position_name?: string;
    workplace_id?: number;
    department_id?: number | null;
    agreement: boolean;
}

const schema = yup.object({
    first_name: yup.string().required('registration.errors.required'),
    last_name: yup.string().required('registration.errors.required'),
    employee_position_name: yup.string().optional(),
    agreement: yup.boolean().oneOf([true], ERROR_MESSAGES.REQUIRED),
    department_id: yup.number().nullable(),
});

type WorkplaceOption = { label: string; value: WorkplaceType } | null;

const MODAL_THRESHOLD = new Date('2026-05-18T00:00:00').getTime();

const evaluateIsOpen = (data: UserData): boolean => {
    const date = data?.last_login_at;
    if (date === null) return true;
    return new Date(date).getTime() < MODAL_THRESHOLD || (data.require ?? []).length > 0;
};

export const useFirstLoginModal = () => {
    const { t } = useTranslation();

    const [isOpen, setIsOpen] = useState(false);
    const [modalUserData, setModalUserData] = useState<UserData | null>(null);
    const [showWorkplaceSelect, setShowWorkplaceSelect] = useState(false);
    const [isDateTriggered, setIsDateTriggered] = useState(false);

    useEffect(() => {
        const handler = (e: Event) => {
            const data = (e as CustomEvent<UserData>).detail;
            if (data) {
                const date = data.last_login_at;
                const dateTrigger = date !== null && date !== undefined && new Date(date).getTime() < MODAL_THRESHOLD;
                setModalUserData(data);
                setIsOpen(evaluateIsOpen(data));
                setIsDateTriggered(dateTrigger);
                setShowWorkplaceSelect((data.workplace_id == null || (data.require ?? []).includes('workplace_id'))|| dateTrigger);
            }
        };
        window.addEventListener('userDataLoaded', handler);
        return () => window.removeEventListener('userDataLoaded', handler);
    }, []);

    const requiredFields: string[] = modalUserData?.require ?? [];
    const hasName = !requiredFields.includes('first_name') && !requiredFields.includes('last_name');

    const initialWorkplaceOption: WorkplaceOption =
        modalUserData?.workplace_id != null && modalUserData?.workplace_name
            ? {
                  label: modalUserData.workplace_name,
                  value: { ...modalUserData, id: modalUserData.workplace_id } as unknown as WorkplaceType,
              }
            : null;

    const [selectedWorkplace, setSelectedWorkplace] = useState<WorkplaceOption>(initialWorkplaceOption);
    const [selectedDepartment, setSelectedDepartment] = useState<{ label: string; value: number } | null>(null);

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
        setError,
        setValue,
        clearErrors,
    } = useForm<FirstLoginFormValues>({
        resolver: yupResolver(schema as any),
        defaultValues: {
            first_name: modalUserData?.first_name || '',
            last_name: modalUserData?.last_name || '',
            phone: modalUserData?.phone || '',
            employee_position_name: modalUserData?.employee_position_name || 'stanowisko',
            workplace_id: modalUserData?.workplace_id,
            department_id: null,
        },
    });

    const currentWorkplaceId = selectedWorkplace?.value?.id;
    const needsDepartment = requiredFields.includes('department_id') || isDateTriggered;
    
    const loadDepartmentOptions = async (search: string, _: any, { page }: { page: number }) => {
        if (!currentWorkplaceId) return { options: [], hasMore: false, additional: { page: 1 } };
        const res = await fetchDepartmentsPaginated(currentWorkplaceId, page);
        return {
            options: res.data.map((d: any) => ({ label: d.name, value: d.id })),
            hasMore: res.current_page < res.last_page,
            additional: { page: page + 1 },
        };
    };

    const mutation = useMutation(updateUser, {
        onSuccess: async () => {
            toast.success(t('registration.successUpdated'));
            setIsOpen(false);
        },
        onError: (error: any) => {
            const formErrors = error?.response?.data?.errors;
            if (formErrors) {
                Object.entries(formErrors).forEach(([key, value]) => {
                    setError(key as keyof FirstLoginFormValues, {
                        message: (value as string[])?.[0],
                    });
                });
            }
        },
    });

    const onSubmit = ({ agreement: _, department_id, ...data }: FirstLoginFormValues) => {
        // runtime validation: if workplace select is required but not provided, show error
        if (showWorkplaceSelect && !data.workplace_id) {
            setError('workplace_id', { message: 'registration.errors.required' });
            return;
        }

        const payload: any = { ...data };
        if (department_id != null) payload.department_id = department_id;
        mutation.mutate(payload);
    };

    const loadWorkplaceOptions = async (search: string, _: any, { page }: { page: number }) => {
        const res = await getWorkplaces({ name: search || undefined, page });
        return {
            options: res.data.map((w: WorkplaceType) => ({
                label: w.workplace_name,
                value: w,
            })),
            hasMore: res.current_page < res.last_page,
            additional: { page: page + 1 },
        };
    };

    const handleWorkplaceChange = (option: WorkplaceOption) => {
        setSelectedWorkplace(option);
        setSelectedDepartment(null);
        setValue('department_id', null);
        if (!option) {
            setValue('workplace_id', null);
            clearErrors && clearErrors('workplace_id');
            return;
        }
        const w = option.value;
        if (w.id) {
            setValue('workplace_id', w.id);
            clearErrors && clearErrors('workplace_id');
        }
    };

    const handleDepartmentChange = (option: { label: string; value: number } | null, fieldOnChange: (val: number | null) => void) => {
        setSelectedDepartment(option);
        fieldOnChange(option?.value ?? null);
    };

    useEffect(() => {
        if (isOpen) document.body.style.overflowY = 'hidden';
        else document.body.style.overflowY = 'auto';
        return () => {
            document.body.style.overflowY = 'auto';
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !modalUserData) return;

        setValue('first_name', modalUserData.first_name || '');
        setValue('last_name', modalUserData.last_name || '');
        setValue('phone', modalUserData.phone || '');
        setValue('employee_position_name', modalUserData.employee_position_name || 'stanowisko');
        if (modalUserData.workplace_id != null) setValue('workplace_id', modalUserData.workplace_id);
        if (modalUserData.workplace_id != null && modalUserData.workplace_name) {
            setSelectedWorkplace({
                label: modalUserData.workplace_name,
                value: { ...modalUserData, id: modalUserData.workplace_id } as unknown as WorkplaceType,
            });
        }

        // W trybie "only department" checkbox jest ukryty – ustawiamy agreement automatycznie
        const req = modalUserData.require ?? [];

        const onlyDept =
            !isDateTriggered &&
            !req.includes('first_name') &&
            !req.includes('last_name') &&
            req.includes('department_id') &&
            !!modalUserData.workplace_id;
        if (onlyDept) {
            setValue('agreement', true);
        }
    }, [isOpen]);

    return {
        t,
        isOpen,
        hasName,
        requiredFields,
        isDateTriggered,
        showWorkplaceSelect,
        register,
        handleSubmit,
        errors,
        onSubmit,
        loadWorkplaceOptions,
        handleWorkplaceChange,
        selectedWorkplace,
        selectedDepartment,
        needsDepartment,
        loadDepartmentOptions,
        handleDepartmentChange,
        control,
        currentWorkplaceId
    };
};
